import React, { useEffect, useState } from "react";
import { adminService } from "../../services/admin.service";
import "./admin.css";

const AdminReviewsPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all"); // all, highRated, lowRated
  const [sortBy, setSortBy] = useState("latest"); // latest, oldest, rating
  const [expandedProducts, setExpandedProducts] = useState({}); // Track which products are expanded

  useEffect(() => {
    loadReviews();
  }, []);

  const loadReviews = async () => {
    try {
      setLoading(true);
      setError("");

      const data = await adminService.getAllReviews();
      setProducts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load reviews:", err);

      let errorMsg = "Failed to load reviews";
      if (err.response?.status === 404) {
        errorMsg = "Reviews endpoint not found. Check backend API.";
      } else if (err.response?.status === 401) {
        errorMsg = "Unauthorized. Please login again.";
      } else if (err.message === "Network Error") {
        errorMsg = "Cannot connect to server. Is the backend running?";
      }

      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const toggleProductExpanded = (productId) => {
    setExpandedProducts((prev) => ({
      ...prev,
      [productId]: !prev[productId],
    }));
  };

  const handleDelete = async (productId, reviewId) => {
    if (!window.confirm("Are you sure you want to delete this review?")) return;

    try {
      await adminService.deleteReview(productId, reviewId);
      loadReviews();
    } catch (err) {
      console.error("Delete failed:", err);
      alert("Failed to delete review");
    }
  };

  const renderStars = (rating = 0) => {
    const r = Number(rating) || 0;
    return Array.from({ length: 5 }, (_, i) => (
      <span key={i} className={i < r ? "star filled" : "star"}>
        ★
      </span>
    ));
  };

  const getFilteredProducts = () => {
    let filtered = products;

    // Apply filter
    if (filter === "highRated") {
      filtered = products.filter((p) => (p.average_rating || 0) >= 4);
    } else if (filter === "lowRated") {
      filtered = products.filter((p) => (p.average_rating || 0) < 3);
    }

    return filtered;
  };

  const getSortedReviews = (reviews) => {
    if (!reviews) return [];
    return [...reviews].sort((a, b) => {
      if (sortBy === "rating") {
        return b.rating - a.rating;
      } else if (sortBy === "oldest") {
        return new Date(a.createdAt) - new Date(b.createdAt);
      }
      // latest (default)
      return new Date(b.createdAt) - new Date(a.createdAt);
    });
  };

  const filteredProducts = getFilteredProducts();
  const totalReviews = products.reduce(
    (sum, p) => sum + (p.reviews?.length || 0),
    0,
  );
  const avgRating =
    products.length > 0
      ? (
          products.reduce((sum, p) => sum + (p.average_rating || 0), 0) /
          products.length
        ).toFixed(1)
      : 0;

  if (loading) {
    return (
      <div className="reviews-loading">
        <div className="spinner"></div>
        <p>Loading reviews...</p>
      </div>
    );
  }

  return (
    <div className="admin-reviews-container">
      {/* Header Section */}
      <div className="reviews-header">
        <div className="reviews-header-content">
          <h1>Product Reviews</h1>
          <p className="reviews-subtitle">
            Manage and monitor customer feedback
          </p>
        </div>

        {/* Stats Cards */}
        <div className="reviews-stats">
          <div className="stat-card">
            <div className="stat-value">{totalReviews}</div>
            <div className="stat-label">Total Reviews</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{products.length}</div>
            <div className="stat-label">Products with Reviews</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{avgRating}</div>
            <div className="stat-label">Average Rating</div>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="alert alert-danger">
          <strong>⚠️ Error:</strong> {error}
        </div>
      )}

      {/* Filter & Sort Controls */}
      <div className="reviews-controls">
        <div className="control-group">
          <label htmlFor="filter">Filter by Rating:</label>
          <select
            id="filter"
            className="control-select"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="all">All Products</option>
            <option value="highRated">High Rated (4+)</option>
            <option value="lowRated">Low Rated (&lt;3)</option>
          </select>
        </div>

        <div className="control-group">
          <label htmlFor="sort">Sort by:</label>
          <select
            id="sort"
            className="control-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="latest">Latest First</option>
            <option value="oldest">Oldest First</option>
            <option value="rating">Highest Rating</option>
          </select>
        </div>

        <button className="btn-refresh" onClick={loadReviews}>
          🔄 Refresh
        </button>
      </div>

      {/* Content Section */}
      {filteredProducts.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📝</div>
          <h3>No reviews found</h3>
          <p>There are currently no reviews matching your filters.</p>
        </div>
      ) : (
        <div className="reviews-grid">
          {filteredProducts.map((product) => (
            <div key={product.product_id} className="review-product-section">
              {/* Product Header - Always Visible */}
              <div
                className="product-header-card"
                onClick={() => toggleProductExpanded(product.product_id)}
              >
                <div className="product-header-wrapper">
                  <div className="product-info">
                    <h3 className="product-name">{product.name}</h3>
                    <div className="product-meta">
                      <div className="rating-badge">
                        <span className="rating-value">
                          {product.average_rating?.toFixed(1) || "0.0"}
                        </span>
                        <div className="stars-container">
                          {renderStars(Math.round(product.average_rating || 0))}
                        </div>
                      </div>
                      <span className="review-count">
                        {product.total_reviews || 0} review
                        {product.total_reviews !== 1 ? "s" : ""}
                      </span>
                    </div>
                  </div>

                  {/* Dropdown Toggle Button */}
                  <div className="expand-button">
                    <span
                      className={`expand-icon ${
                        expandedProducts[product.product_id] ? "expanded" : ""
                      }`}
                    >
                      ▼
                    </span>
                  </div>
                </div>
              </div>

              {/* Reviews List - Expandable */}
              {expandedProducts[product.product_id] && (
                <div className="reviews-list-collapsible">
                  {product.reviews && product.reviews.length > 0 ? (
                    <div className="reviews-list">
                      {getSortedReviews(product.reviews).map((review) => (
                        <div key={review._id} className="review-item">
                          <div className="review-card-header">
                            <div className="reviewer-info">
                              <span className="reviewer-name">
                                {review.name}
                              </span>
                              <span className="review-date">
                                {review.createdAt
                                  ? new Date(
                                      review.createdAt,
                                    ).toLocaleDateString()
                                  : "N/A"}
                              </span>
                            </div>
                            <div className="review-rating">
                              {renderStars(review.rating)}
                            </div>
                          </div>

                          <p className="review-comment">{review.comment}</p>

                          <div className="review-footer">
                            <button
                              className="btn-delete"
                              onClick={() =>
                                handleDelete(product.product_id, review._id)
                              }
                            >
                              🗑️ Delete Review
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="no-reviews">No reviews for this product</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminReviewsPage;
